import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import { EvalReviewPage } from './pages/EvalReview';
import { RequireAuth } from './auth/session';
import { AppShell } from './components/layout/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdminPage } from './pages/Admin';
import { ApprovalsPage } from './pages/Approvals';
import { AskPage } from './pages/Ask';
import { AuditLogPage } from './pages/AuditLog';
import { BotRulesPage } from './pages/BotRules';
import { CorrectionsPage } from './pages/Corrections';
import { EvalRunsPage } from './pages/EvalRuns';
import { InboxPage } from './pages/InboxPage';
import { KnowledgePage } from './pages/Knowledge';
import { PlaygroundPage } from './pages/Playground';
import { PlaygroundLogPage } from './pages/PlaygroundLog';
import { PromptsPage } from './pages/Prompts';
import { ProposalsPage } from './pages/Proposals';
import { QaReviewsPage } from './pages/QaReviews';
import { SignIn } from './pages/SignIn';
import { TaxonomiesPage } from './pages/Taxonomies';
import { TicketWorkspace } from './pages/TicketWorkspace';
import { WorkflowsPage } from './pages/Workflows';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

/**
 * Loaded on demand. The workbook parser (exceljs) is about a megabyte, and it
 * is only needed by the person importing QA material — everyone else opens the
 * inbox all day and should not pay for it.
 */
const EvalsPage = lazy(() =>
  import('./pages/Evals').then((module) => ({ default: module.EvalsPage })),
);

// Same reason: recharts is a few hundred kilobytes only this screen needs —
// and the Dashboard is now the chart screen, Reports having merged into it.
const DashboardPage = lazy(() =>
  import('./pages/dashboard').then((module) => ({ default: module.DashboardPage })),
);

// Engine: superadmin tech metrics, also chart-heavy, also on demand.
const EnginePage = lazy(() =>
  import('./pages/engine').then((module) => ({ default: module.EnginePage })),
);

// The Placeholder component that used to live here is gone: every route in the
// console now renders a real screen.

/** Keyed by path so navigating away from a crashed screen resets the fence. */
function ScreenBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          {/*
            Everything below this line shows customer data, so it all sits
            behind RequireAuth. The guard is on the WRAPPER rather than on each
            screen: a new route added inside is protected by default, and
            forgetting to protect one is not possible.
          */}
          <Route
            path="*"
            element={
              <RequireAuth>
                <AppShell>
                  <ScreenBoundary>
                  <Routes>
                    {/* Landing on insight, not work: the Dashboard is the
                        default screen after sign-in (Deivydas 2026-09-18). */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/inbox" element={<InboxPage />} />
                    <Route path="/tickets/:id" element={<TicketWorkspace />} />
                    <Route path="/approvals" element={<ApprovalsPage />} />
                    <Route
                      path="/dashboard"
                      element={
                        <Suspense
                          fallback={
                            <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>
                          }
                        >
                          <DashboardPage />
                        </Suspense>
                      }
                    />
                    {/* Reports merged into the Dashboard; old links still land. */}
                    <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
                    <Route
                      path="/engine"
                      element={
                        <Suspense
                          fallback={
                            <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>
                          }
                        >
                          <EnginePage />
                        </Suspense>
                      }
                    />
                    <Route path="/ask" element={<AskPage />} />
                    <Route path="/audit" element={<AuditLogPage />} />
                    <Route path="/workflows" element={<WorkflowsPage />} />
                    <Route path="/rules" element={<BotRulesPage />} />
                    <Route path="/taxonomies" element={<TaxonomiesPage />} />
                    <Route path="/playground" element={<PlaygroundPage />} />
                    <Route path="/playground-log" element={<PlaygroundLogPage />} />
                    <Route path="/knowledge" element={<KnowledgePage />} />
                    <Route path="/qa" element={<QaReviewsPage />} />
                    <Route path="/corrections" element={<CorrectionsPage />} />
                    <Route path="/proposals" element={<ProposalsPage />} />
                    <Route
                      path="/evals"
                      element={
                        <Suspense
                          fallback={
                            <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>
                          }
                        >
                          <EvalsPage />
                        </Suspense>
                      }
                    />
                    <Route path="/evals/review" element={<EvalReviewPage />} />
                    <Route path="/evals/runs" element={<EvalRunsPage />} />
                    <Route path="/prompts" element={<PromptsPage />} />
                    <Route
                      path="/admin"
                      element={<AdminPage />}
                    />
                  </Routes>
                  </ScreenBoundary>
                </AppShell>
              </RequireAuth>
            }
          />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}
